// src/pages/RestaurantOwner/InvoiceManagementPanel.jsx - MANAGE INVOICES

import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, Loader, AlertCircle, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import StorageService from '../../services/StorageService';
import InvoiceDownloadModal from '../../components/InvoiceDownloadModal';
import InvoiceCancellationModal from '../../components/InvoiceCancellationModal';
import Receipt from '../../components/Receipt';

const InvoiceManagementPanel = ({ restaurantId }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState(null); 
  const [filterType, setFilterType] = useState('active'); // active, cancelled, all
  const [searchTerm, setSearchTerm] = useState('');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadInvoices();
  }, [restaurantId]);

  const loadInvoices = async () => {
  try {
    setLoading(true);
    const [allInvoices, restaurantData, stats] = await Promise.all([
      StorageService.getInvoices(restaurantId),
      StorageService.getRestaurant(restaurantId),  // ADD THIS LINE
      StorageService.getRevenueStats(restaurantId)
    ]);
    
    setInvoices(allInvoices);
    setRestaurant(restaurantData);  // ADD THIS LINE
    setStats(stats);
  } catch (error) {
    console.error('Error loading invoices:', error);
  } finally {
    setLoading(false);
  }
};

  const filteredInvoices = invoices.filter(invoice => {
    // Filter by type
    if (filterType === 'active' && invoice.cancelled) return false;
    if (filterType === 'cancelled' && !invoice.cancelled) return false;

    // Search filter
    if (searchTerm) {
      return (
        invoice.invoiceNumber.includes(searchTerm) ||
        invoice.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.tableName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return true;
  });

  const getStatusBadge = (invoice) => {
    if (invoice.cancelled) {
      return (
        <div className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">
          <XCircle className="w-3 h-3" />
          Cancelled
        </div>
      );
    }
    if (invoice.printed) {
      return (
        <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
          <CheckCircle className="w-3 h-3" />
          Printed
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-semibold">
        <AlertCircle className="w-3 h-3" />
        Pending Print
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  if (!loading && restaurant) {
    const plan = restaurant.package?.toLowerCase();
    const hasInvoiceAccess = plan === 'premium' || plan === 'advanced' ||plan==='basic';
    if (!hasInvoiceAccess) {
        return (
            <div className="p-6 min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50">
                <div className="max-w-2xl mx-auto mt-20">
                    <div className="bg-white rounded-2xl shadow-xl p-8 text-center border-4 border-orange-200">
                        <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <FileText className="w-10 h-10 text-red-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800 mb-4">Invoice Management Access Restricted</h2>
                        <p className="text-lg text-gray-600 mb-2">
                            You are currently on the <span className="font-bold text-orange-600">{restaurant.package || 'Basic'} Plan</span>
                        </p>
                        <p className="text-gray-600 mb-8">
                            Invoice Management Panel is only available for <span className="font-bold">Premium</span> and <span className="font-bold">Advanced</span> plan users.
                        </p>
                        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 mb-8">
                            <h3 className="font-bold text-gray-800 mb-4">Upgrade to access:</h3>
                            <ul className="text-left space-y-2 text-gray-700">
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    View All Invoice History
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Search &amp; Filter Invoices
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Cancel Invoices with Audit Trail
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Download Invoice Reports
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Revenue Statistics Dashboard
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Reprint Old Invoices
                                </li>
                            </ul>
                        </div>
                        <a
                            href={'mailto:contact@zyppro.com?subject=Upgrade%20to%20Premium%20Plan&body=Hi%20Zyppro%20Team,%0D%0A%0D%0AI%20would%20like%20to%20upgrade%20to%20Premium%20or%20Advanced%20plan%20to%20access%20Invoice%20Management.%0D%0A%0D%0ARestaurant:%20'+ encodeURIComponent(restaurant.name) + '%0D%0ACurrent%20Plan:%20' + encodeURIComponent(restaurant.package || 'Basic') +'%0D%0A%0D%0APlease%20contact%20me.%0D%0A%0D%0AThank%20you'}
                            className="inline-flex items-center gap-2 bg-orange-500 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-orange-600 transition shadow-lg"
                        >
                            <FileText className="w-6 h-6" />
                            Contact Us to Upgrade
                        </a>
                        <p className="text-sm text-gray-600 mt-4">
                            Email: <a href="mailto:contact@zyppro.com" className="text-orange-600 underline font-semibold">contact@zyppro.com</a>
                        </p>
                    </div>
                </div>
            </div>
        );
    }
}

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <FileText className="w-8 h-8 text-orange-500" />
          Invoice Management
        </h1>
        <button
          onClick={() => setShowDownloadModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
        >
          <Download className="w-5 h-5" />
          Download Invoices
        </button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-600 text-sm mb-1">Total Invoices</p>
            <p className="text-3xl font-bold text-gray-800">{stats.count}</p>
            <p className="text-xs text-gray-500 mt-1">
              Active: {stats.count - (invoices.filter(i => i.cancelled).length || 0)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-600 text-sm mb-1">Total Revenue</p>
            <p className="text-3xl font-bold text-orange-600">₹{stats.total.toFixed(0)}</p>
            <p className="text-xs text-gray-500 mt-1">Average: ₹{stats.averageOrderValue.toFixed(0)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-600 text-sm mb-1">Tax Collected</p>
            <p className="text-3xl font-bold text-blue-600">₹{stats.taxes.toFixed(0)}</p>
            <p className="text-xs text-gray-500 mt-1">CGST+SGST+SC</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-600 text-sm mb-1">Pending Print</p>
            <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
            <p className="text-xs text-gray-500 mt-1">Printed: {stats.printed}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by invoice, customer, or table..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('active')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                filterType === 'active'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setFilterType('cancelled')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                filterType === 'cancelled'
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Cancelled
            </button>
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                filterType === 'all'
                  ? 'bg-gray-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
          </div>
        </div>
      </div>

      {/* Invoices List */}
      {filteredInvoices.length > 0 ? (
        <div className="space-y-3">
          {filteredInvoices.map(invoice => (
            <div key={invoice.id} className="bg-white rounded-lg shadow p-4 hover:shadow-md transition">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-center">
                {/* Invoice Number */}
                <div>
                  <p className="text-xs text-gray-600">Invoice #</p>
                  <p className="font-bold text-gray-800">{invoice.invoiceNumber}</p>
                </div>

                {/* Date */}
                <div>
                  <p className="text-xs text-gray-600">Date</p>
                  <p className="text-sm text-gray-800">{invoice.date}</p>
                  <p className="text-xs text-gray-500">{invoice.time}</p>
                </div>

                {/* Customer & Table */}
                <div>
                  <p className="text-xs text-gray-600">Customer / Table</p>
                  <p className="text-sm font-semibold text-gray-800">{invoice.customerName}</p>
                  <p className="text-xs text-gray-500">{invoice.tableName}</p>
                </div>

                {/* Amount */}
                <div>
                  <p className="text-xs text-gray-600">Amount</p>
                  <p className="text-lg font-bold text-orange-600">₹{invoice.grandTotal.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">Sub: ₹{invoice.subtotal.toFixed(0)}</p>
                </div>

                {/* Status */}
                <div>
                  {getStatusBadge(invoice)}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedInvoice(invoice);
                      setShowReceipt(true);
                    }}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-xs md:text-sm"
                    title="View Invoice"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="hidden md:inline">View</span>
                  </button>

                  {!invoice.cancelled && (
                    <button
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setShowCancellationModal(true);
                      }}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-xs md:text-sm"
                      title="Cancel Invoice"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="hidden md:inline">Cancel</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Cancellation Info */}
              {invoice.cancelled && (
                <div className="mt-3 pt-3 border-t border-red-200 bg-red-50 p-2 rounded text-xs text-red-700">
                  <p className="font-semibold">Cancelled on {new Date(invoice.cancelledAt).toLocaleString('en-IN')}</p>
                  <p>Reason: {invoice.cancellationReason}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">No invoices found</p>
        </div>
      )}

      {/* Download Modal */}
      {showDownloadModal && (
        <InvoiceDownloadModal
          restaurantId={restaurantId}
          onClose={() => setShowDownloadModal(false)}
        />
      )}

      {/* Cancellation Modal */}
      {showCancellationModal && selectedInvoice && (
        <InvoiceCancellationModal
          invoice={selectedInvoice}
          restaurantId={restaurantId}
          onClose={() => {
            setShowCancellationModal(false);
            setSelectedInvoice(null);
          }}
          onCancelled={() => {
            loadInvoices();
          }}
        />
      )}

      {/* Receipt Modal */}
      {showReceipt && selectedInvoice && (
        <Receipt
          invoice={selectedInvoice}
          onClose={() => {
            setShowReceipt(false);
            setSelectedInvoice(null);
          }}
          onPrint={async () => {
            try {
              await StorageService.markInvoiceAsPrinted(restaurantId, selectedInvoice.invoiceNumber);
              loadInvoices();
            } catch (error) {
              console.error('Error marking as printed:', error);
            }
          }}
        />
      )}
    </div>
  );
};

export default InvoiceManagementPanel;